import { env } from '../config/env'

interface AuthTokenResponse {
  access_token?: string
  token?: string
  token_type?: string
  expires_in?: number
  userName?: string
  username?: string
}

type GoogleTokenClientConfig = {
  client_id: string
  scope: string
  callback: (tokenResponse: { access_token?: string; error?: string }) => void
  error_callback?: (error: { type?: string; message?: string }) => void
  errorCallback?: (error: { type?: string; message?: string }) => void
}

type GoogleNamespace = {
  accounts?: {
    oauth2?: {
      initTokenClient: (config: GoogleTokenClientConfig) => { requestAccessToken: () => void }
    }
  }
}

const apiPath = (path: string): string => `${env.apiBase}${path}`
const GOOGLE_GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client'
const GOOGLE_SCOPE = 'openid email profile'
const GOOGLE_PROVIDER = 'Google'
const FILAZERO_EXTERNAL_LOGIN_PATH = '/api/account/registerOrUpdateExternalLogin'
const CLIENT_ID_FOR_API = 1

function extractErrorMessage(raw: string): string {
  try {
    const parsed = JSON.parse(raw) as { message?: string; error?: string; error_description?: string }
    return parsed.message ?? parsed.error_description ?? parsed.error ?? ''
  } catch {
    return raw
  }
}

async function tryLogin(email: string, password: string): Promise<AuthTokenResponse> {
  const body = new URLSearchParams({
    grant_type: 'password',
    client_id: env.clientId,
    userName: email,
    password,
  })
  const response = await fetch(apiPath('/token'), {
    method: 'POST',
    headers: {
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: body.toString(),
  })
  const raw = await response.text()
  if (!response.ok) {throw new Error(extractErrorMessage(raw) || `HTTP ${response.status}`)}
  try {
    return JSON.parse(raw) as AuthTokenResponse
  } catch {
    throw new Error('Resposta inesperada do servidor de autenticação.')
  }
}

export async function loginWithPassword(email: string, password: string): Promise<string> {
  const tokenResponse = await tryLogin(email, password)
  const token = tokenResponse.access_token ?? tokenResponse.token
  if (token) {return token}
  throw new Error('Resposta sem access_token.')
}

export function isGoogleLoginConfigured(): boolean {
  return Boolean(env.googleClientId)
}

function loadGoogleIdentityServices(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('Ambiente sem documento.'))
      return
    }

    const google = (globalThis as Record<string, unknown>).google as GoogleNamespace | undefined
    if (google?.accounts?.oauth2) {
      resolve()
      return
    }

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GOOGLE_GIS_SCRIPT_SRC}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('Falha ao carregar Google Identity Services.')))
      return
    }

    const script = document.createElement('script')
    script.src = GOOGLE_GIS_SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Falha ao carregar Google Identity Services.'))
    document.head.appendChild(script)
  })
}

export function preloadGoogleLogin(): void {
  if (!isGoogleLoginConfigured()) {return}
  void loadGoogleIdentityServices().catch(() => {})
}

function requestGoogleAccessToken(): Promise<string> {
  return new Promise((resolve, reject) => {
    const google = (globalThis as Record<string, unknown>).google as GoogleNamespace | undefined
    if (!google?.accounts?.oauth2) {
      reject(new Error('Google Identity Services não carregado.'))
      return
    }

    const client = google.accounts.oauth2.initTokenClient({
      client_id: env.googleClientId,
      scope: GOOGLE_SCOPE,
      callback: (tokenResponse) => {
        if (tokenResponse.error || !tokenResponse.access_token) {
          reject(new Error('Não foi possível concluir o login com Google. Tente novamente.'))
          return
        }
        resolve(tokenResponse.access_token)
      },
      error_callback: () => reject(new Error('Não foi possível concluir o login com Google. Tente novamente.')),
      errorCallback: () => reject(new Error('Não foi possível concluir o login com Google. Tente novamente.')),
    })

    client.requestAccessToken()
  })
}

async function exchangeGoogleTokenWithFilazero(googleAccessToken: string): Promise<AuthTokenResponse> {
  const response = await fetch(apiPath(FILAZERO_EXTERNAL_LOGIN_PATH), {
    method: 'POST',
    headers: {
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/json;charset=UTF-8',
    },
    body: JSON.stringify({
      externalAccessToken: googleAccessToken,
      provider: GOOGLE_PROVIDER,
      clientId: CLIENT_ID_FOR_API,
    }),
  })

  const raw = await response.text()
  if (!response.ok) {throw new Error(extractErrorMessage(raw) || 'Falha ao autenticar com o servidor Filazero.')}

  try {
    const parsed = JSON.parse(raw) as AuthTokenResponse | { responseData?: AuthTokenResponse }
    if ('responseData' in parsed && parsed.responseData?.access_token) {return parsed.responseData}
    return parsed as AuthTokenResponse
  } catch {
    throw new Error('Resposta inesperada do servidor de autenticação.')
  }
}

export async function loginWithGoogle(): Promise<string> {
  if (!isGoogleLoginConfigured()) {throw new Error('Login com Google não configurado.')}
  await loadGoogleIdentityServices()
  const googleAccessToken = await requestGoogleAccessToken()
  const tokenResponse = await exchangeGoogleTokenWithFilazero(googleAccessToken)
  const token = tokenResponse.access_token ?? tokenResponse.token
  if (token) {return token}
  throw new Error('Resposta sem access_token.')
}
